import React, { useState, useEffect, useCallback } from "react";
import { Sun, Moon, CheckCircle, XCircle, RefreshCw, Eye, EyeOff, Bell, BellOff } from "react-feather";
import { useTheme } from "../theme/ThemeContext";
import { useSettings } from "../settings/SettingsContext";
import GroupsPanel from "../groups/GroupsPanel";
import DangerZone from "../settings/DangerZone";

import { API_BASE, apiFetch } from "../api";

const DATA_SECTIONS = [
  { key: "bills", label: "Bills & Payments", endpoint: "bills" },
  { key: "income", label: "Income", endpoint: "income" },
  { key: "loans", label: "Loans", endpoint: "loans" },
  { key: "assets", label: "Assets", endpoint: "assets" },
  { key: "cards", label: "Credit Cards", endpoint: "credit-cards" },
  { key: "plans", label: "Budget Plans", endpoint: "budget-plans" },
];

const TABS = ["Data", "Profile", "Groups", "Privacy", "Account"];

const Settings = () => {
  const { theme, toggleTheme, tokens, fonts } = useTheme();
  const { settings, updateSettings } = useSettings();

  const [activeTab, setActiveTab] = useState("Data");

  return (
    <div style={{ ...styles.page, backgroundColor: tokens.bg, fontFamily: fonts.body }}>
      <h2 style={{ ...styles.heading, fontFamily: fonts.display, color: tokens.textPrimary }}>Settings</h2>
      <p style={{ ...styles.subheading, color: tokens.textMuted }}>
        App preferences, your profile, and what's shared on screen.
      </p>

      <div style={styles.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              ...styles.tabBtn,
              backgroundColor: activeTab === tab ? tokens.accent : tokens.neutralSoft,
              color: activeTab === tab ? tokens.accentContrast : tokens.textPrimary,
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === "Data" && <DataTab tokens={tokens} fonts={fonts} theme={theme} toggleTheme={toggleTheme} profileEmail={settings.profileEmail} />}
      {activeTab === "Profile" && <ProfileTab tokens={tokens} fonts={fonts} settings={settings} updateSettings={updateSettings} />}
      {activeTab === "Groups" && <GroupsPanel tokens={tokens} fonts={fonts} />}
      {activeTab === "Privacy" && <PrivacyTab tokens={tokens} fonts={fonts} settings={settings} updateSettings={updateSettings} />}
      {activeTab === "Account" && (
        <div style={{ maxWidth: 520 }}>
          <p style={{ ...styles.subheading, color: tokens.textMuted }}>Start over with an empty account, or delete it. Both ask for your password.</p>
          <DangerZone onReset={() => window.location.reload()} />
        </div>
      )}
    </div>
  );
};

// Only this account sees the raw API URL and connection health -- everyone
// else just sees the app.
const BACKEND_INFO_EMAIL = "lalitkumar281189@gmail.com";

const DataTab = ({ tokens, fonts, theme, toggleTheme, profileEmail }) => {
  const showBackendInfo = (profileEmail || "").trim().toLowerCase() === BACKEND_INFO_EMAIL;
  const [connection, setConnection] = useState("checking"); // checking | connected | failed
  const [counts, setCounts] = useState(null);
  const [loadingCounts, setLoadingCounts] = useState(true);

  const checkConnection = useCallback(() => {
    setConnection("checking");
    apiFetch(`${API_BASE}/health`)
      .then((res) => (res.ok ? setConnection("connected") : setConnection("failed")))
      .catch(() => setConnection("failed"));
  }, []);

  const loadCounts = useCallback(() => {
    setLoadingCounts(true);
    Promise.all(
      DATA_SECTIONS.map((s) => apiFetch(`${API_BASE}/${s.endpoint}`).then((res) => (res.ok ? res.json() : [])))
    )
      .then((results) => {
        const next = {};
        DATA_SECTIONS.forEach((s, i) => { next[s.key] = results[i].length; });
        setCounts(next);
      })
      .catch(() => setCounts(null))
      .finally(() => setLoadingCounts(false));
  }, []);

  useEffect(() => {
    if (showBackendInfo) checkConnection();
    loadCounts();
  }, [showBackendInfo, checkConnection, loadCounts]);

  return (
    <div style={styles.sections}>
      <div style={{ ...styles.card, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
        <h3 style={{ ...styles.cardTitle, fontFamily: fonts.display, color: tokens.textPrimary }}>Appearance</h3>
        <div style={styles.row}>
          <div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: tokens.textPrimary }}>Theme</p>
            <p style={{ margin: "2px 0 0", fontSize: 13, color: tokens.textMuted }}>
              {theme === "light" ? "Sprint Energy (light)" : "Midnight Trader (dark)"}
            </p>
          </div>
          <button onClick={toggleTheme} style={{ ...styles.actionBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}>
            {theme === "light" ? <Moon size={15} /> : <Sun size={15} />}
            Switch to {theme === "light" ? "Dark" : "Light"}
          </button>
        </div>
      </div>

      {showBackendInfo && (
      <div style={{ ...styles.card, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
        <h3 style={{ ...styles.cardTitle, fontFamily: fonts.display, color: tokens.textPrimary }}>Backend Connection</h3>
        <div style={styles.row}>
          <div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: tokens.textPrimary }}>{API_BASE}</p>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
              {connection === "checking" && <p style={{ margin: 0, fontSize: 13, color: tokens.textMuted }}>Checking...</p>}
              {connection === "connected" && (
                <>
                  <CheckCircle size={14} color={tokens.positive} />
                  <span style={{ fontSize: 13, color: tokens.positive, fontWeight: 600 }}>Connected</span>
                </>
              )}
              {connection === "failed" && (
                <>
                  <XCircle size={14} color={tokens.negative} />
                  <span style={{ fontSize: 13, color: tokens.negative, fontWeight: 600 }}>
                    Not reachable &mdash; run it with `npm run server`
                  </span>
                </>
              )}
            </div>
          </div>
          <button
            onClick={() => { checkConnection(); loadCounts(); }}
            style={{ ...styles.iconOnlyBtn, borderColor: tokens.surfaceBorder, color: tokens.textPrimary }}
            aria-label="Recheck connection"
          >
            <RefreshCw size={15} />
          </button>
        </div>
      </div>
      )}

      <div style={{ ...styles.card, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
        <h3 style={{ ...styles.cardTitle, fontFamily: fonts.display, color: tokens.textPrimary }}>Your Data</h3>
        {loadingCounts && <p style={{ fontSize: 13, color: tokens.textMuted }}>Loading...</p>}
        {!loadingCounts && !counts && <p style={{ fontSize: 13, color: tokens.negative }}>Couldn't load data counts.</p>}
        {!loadingCounts && counts && (
          <div style={styles.countGrid}>
            {DATA_SECTIONS.map((s) => (
              <div key={s.key} style={{ ...styles.countTile, backgroundColor: tokens.neutralSoft }}>
                <span style={{ fontFamily: fonts.mono, fontSize: 20, fontWeight: 700, color: tokens.textPrimary }}>
                  {counts[s.key]}
                </span>
                <span style={{ fontSize: 12, color: tokens.textMuted, fontWeight: 600 }}>{s.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ProfileTab = ({ tokens, fonts, settings, updateSettings }) => {
  const [name, setName] = useState(settings.profileName);
  const [email, setEmail] = useState(settings.profileEmail);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    setName(settings.profileName);
    setEmail(settings.profileEmail);
  }, [settings.profileName, settings.profileEmail]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaveError("");
    const result = await updateSettings({ profileName: name, profileEmail: email });
    if (!result.ok) return setSaveError(result.error);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  return (
    <div style={styles.sections}>
      <form
        onSubmit={handleSave}
        style={{ ...styles.card, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}
      >
        <h3 style={{ ...styles.cardTitle, fontFamily: fonts.display, color: tokens.textPrimary }}>Profile</h3>
        <div style={styles.formGrid}>
          <label style={styles.field}>
            <span style={{ ...styles.fieldLabel, color: tokens.textMuted }}>Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              style={{ ...styles.input, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.bg }}
            />
          </label>
          <label style={styles.field}>
            <span style={{ ...styles.fieldLabel, color: tokens.textMuted }}>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={{ ...styles.input, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.bg }}
            />
          </label>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
          <button type="submit" style={{ ...styles.actionBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}>
            Save Profile
          </button>
          {saved && <span style={{ fontSize: 13, color: tokens.positive, fontWeight: 600 }}>Saved</span>}
          {saveError && <span role="alert" style={{ fontSize: 13, color: tokens.negative, fontWeight: 600 }}>{saveError}</span>}
        </div>
        <p style={{ fontSize: 12, color: tokens.textMuted, marginTop: 14, marginBottom: 0 }}>
          Your email is unique to your account. You can use it to sign in, and other people can find you by it when they build a group.
        </p>
      </form>
    </div>
  );
};

const ToggleRow = ({ tokens, fonts, icon: onIcon, iconOff: offIcon, title, description, value, onChange }) => {
  const Icon = value ? onIcon : offIcon;
  return (
    <div style={{ ...styles.card, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
      <div style={styles.row}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ ...styles.toggleIcon, backgroundColor: tokens.neutralSoft, color: tokens.textPrimary }}>
            <Icon size={16} />
          </span>
          <div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: tokens.textPrimary, fontFamily: fonts.display }}>{title}</p>
            <p style={{ margin: "2px 0 0", fontSize: 13, color: tokens.textMuted, maxWidth: 360 }}>{description}</p>
          </div>
        </div>
        <button
          onClick={() => onChange(!value)}
          role="switch"
          aria-checked={value}
          style={{
            ...styles.switchTrack,
            backgroundColor: value ? tokens.accent : tokens.neutralSoft,
          }}
        >
          <span
            style={{
              ...styles.switchThumb,
              backgroundColor: tokens.surface,
              transform: value ? "translateX(18px)" : "translateX(2px)",
            }}
          />
        </button>
      </div>
    </div>
  );
};

const PrivacyTab = ({ tokens, fonts, settings, updateSettings }) => {
  return (
    <div style={styles.sections}>
      <ToggleRow
        tokens={tokens}
        fonts={fonts}
        icon={EyeOff}
        iconOff={Eye}
        title="Privacy Mode"
        description="Hide all money amounts on the Dashboard behind ●●●● — handy when someone's looking over your shoulder."
        value={settings.maskAmounts}
        onChange={(next) => updateSettings({ maskAmounts: String(next) })}
      />
      <ToggleRow
        tokens={tokens}
        fonts={fonts}
        icon={Bell}
        iconOff={BellOff}
        title="Autopay Reminders"
        description="Show a popup when the app opens if an autopay bill is overdue and hasn't been confirmed."
        value={settings.autopayReminders}
        onChange={(next) => updateSettings({ autopayReminders: String(next) })}
      />
    </div>
  );
};

const styles = {
  page: {
    padding: "24px",
    minHeight: "100vh",
    transition: "background-color 0.2s ease",
  },
  heading: {
    fontSize: "24px",
    fontWeight: "700",
    margin: 0,
  },
  subheading: {
    fontSize: "14px",
    margin: "6px 0 20px",
  },
  tabBar: {
    display: "flex",
    gap: 8,
    marginBottom: 20,
  },
  tabBtn: {
    border: "none",
    borderRadius: 999,
    padding: "9px 18px",
    fontSize: 13.5,
    fontWeight: 700,
    cursor: "pointer",
  },
  sections: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
    maxWidth: 620,
  },
  card: {
    padding: "22px 24px",
    borderRadius: 16,
    transition: "background-color 0.2s ease, border-color 0.2s ease",
  },
  cardTitle: {
    margin: "0 0 16px",
    fontSize: 16,
    fontWeight: 700,
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  actionBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: "none",
    borderRadius: 10,
    padding: "10px 16px",
    fontSize: 13.5,
    fontWeight: 700,
    cursor: "pointer",
  },
  iconOnlyBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: "1px solid",
    background: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },
  countGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 12,
  },
  countTile: {
    borderRadius: 12,
    padding: "14px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 16,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  input: {
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid",
    fontSize: 14,
  },
  toggleIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  switchTrack: {
    width: 40,
    height: 22,
    borderRadius: 999,
    border: "none",
    position: "relative",
    cursor: "pointer",
    flexShrink: 0,
    padding: 0,
  },
  switchThumb: {
    position: "absolute",
    top: 2,
    left: 0,
    width: 18,
    height: 18,
    borderRadius: "50%",
    transition: "transform 0.15s ease",
  },
};

export default Settings;
